// 🎨 Selector de Temas con Preview
import { Palette, Check } from 'lucide-react';
import { Button } from '../ui/Button';
import { useThemeStore } from '@/stores/theme.store';
import { THEMES, type Theme } from '@/lib/constants/themes';
import { useTranslation } from '@/lib/hooks/useTranslation';
import { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

export function ThemeSelector() {
  const { currentThemeId, setTheme } = useThemeStore();
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Cerrar dropdown al hacer click fuera
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  const handleThemeChange = (themeId: string) => {
    setTheme(themeId);
    setIsOpen(false);
  };

  // Marca con solo su pareja claro/oscuro (TrackConstruction): basta el botón de modo oscuro
  if (THEMES.length <= 2) return null;

  // Agrupar temas por categoría
  const lightThemes = THEMES.filter((theme) => theme.category === 'light');
  const darkThemes = THEMES.filter((theme) => theme.category === 'dark');

  return (
    <div className="relative" ref={dropdownRef}>
      <Button
        variant="ghost"
        size="sm"
        onClick={() => setIsOpen(!isOpen)}
        className="gap-2"
      >
        <Palette className="w-4 h-4" />
        <span className="hidden sm:inline">{t('settings.theme')}</span>
      </Button>

      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: -10, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -10, scale: 0.95 }}
            transition={{ duration: 0.15 }}
            className="absolute right-0 mt-2 w-80 rounded-lg border border-border bg-popover p-4 shadow-xl z-50 max-h-[600px] overflow-y-auto"
          >
            <div className="mb-4">
              <h3 className="text-sm font-semibold text-foreground">
                {t('settings.selectTheme')}
              </h3>
              <p className="text-xs text-muted-foreground mt-1">
                {t('themes.categories.light')} & {t('themes.categories.dark')}
              </p>
            </div>

            {/* Temas Claros */}
            {lightThemes.length > 0 && (
              <div className="mb-4">
                <h4 className="text-xs font-medium text-muted-foreground mb-2 uppercase">
                  {t('themes.categories.light')}
                </h4>
                <div className="grid grid-cols-2 gap-2">
                  {lightThemes.map((theme) => (
                    <ThemeCard
                      key={theme.id}
                      theme={theme}
                      isSelected={currentThemeId === theme.id}
                      onClick={() => handleThemeChange(theme.id)}
                    />
                  ))}
                </div>
              </div>
            )}

            {/* Temas Oscuros */}
            {darkThemes.length > 0 && (
              <div>
                <h4 className="text-xs font-medium text-muted-foreground mb-2 uppercase">
                  {t('themes.categories.dark')}
                </h4>
                <div className="grid grid-cols-2 gap-2">
                  {darkThemes.map((theme) => (
                    <ThemeCard
                      key={theme.id}
                      theme={theme}
                      isSelected={currentThemeId === theme.id}
                      onClick={() => handleThemeChange(theme.id)}
                    />
                  ))}
                </div>
              </div>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

interface ThemeCardProps {
  theme: Theme;
  isSelected: boolean;
  onClick: () => void;
}

function ThemeCard({ theme, isSelected, onClick }: ThemeCardProps) {
  const { t } = useTranslation();

  // Convertir HSL string a color CSS
  const getPrimaryColor = () => {
    const [h, s, l] = theme.colors.primary.split(' ');
    return `hsl(${h}, ${s}, ${l})`;
  };

  const getBackgroundColor = () => {
    const [h, s, l] = theme.colors.background.split(' ');
    return `hsl(${h}, ${s}, ${l})`;
  };

  return (
    <button
      onClick={onClick}
      className={`
        relative group rounded-lg border-2 transition-all duration-200
        ${
          isSelected
            ? 'border-primary shadow-md'
            : 'border-border hover:border-muted-foreground/50'
        }
      `}
    >
      {/* Preview del tema */}
      <div
        className="h-16 rounded-t-md p-2 flex items-start justify-between"
        style={{ backgroundColor: getBackgroundColor() }}
      >
        <div className="flex gap-1">
          <div
            className="w-2 h-2 rounded-full"
            style={{ backgroundColor: getPrimaryColor() }}
          />
          <div
            className="w-2 h-2 rounded-full opacity-50"
            style={{ backgroundColor: getPrimaryColor() }}
          />
          <div
            className="w-2 h-2 rounded-full opacity-30"
            style={{ backgroundColor: getPrimaryColor() }}
          />
        </div>
        {isSelected && (
          <div
            className="w-5 h-5 rounded-full flex items-center justify-center text-white"
            style={{ backgroundColor: getPrimaryColor() }}
          >
            <Check className="w-3 h-3" />
          </div>
        )}
      </div>

      {/* Nombre del tema */}
      <div className="px-2 py-2 bg-background/50 rounded-b-md">
        <p className="text-xs font-medium text-foreground truncate">
          {t(`themes.${theme.id}`)}
        </p>
      </div>
    </button>
  );
}
